/**
 * Shared branded PDF/Excel report template — WEPL HR Portal
 *
 * STANDARDS (all reports MUST follow):
 *
 *   Page format .... A4 only
 *   Margin ......... 14mm on all sides (STANDARD_MARGIN)
 *   Table width .... Fills full width between margins (no centering a narrow table)
 *   Orientation .... Landscape for wide tables (>10 cols), portrait otherwise
 *
 *   Header ......... Logo (28x14mm, top-left) | Title (16pt bold, centered)
 *                    | Company name (8pt gray, centered)
 *   Metadata ....... 9pt, two-column key-value block below header
 *   Table header ... Red [220,38,38] fill, white text, 7pt bold, center-aligned
 *   Table body ..... 7pt, grid theme, light gray grid lines
 *   Footer ......... "Computer-generated report" + confidentiality + page X of Y
 *
 *   Column alignment (use ALIGN constants in columnStyles.halign):
 *     Text / names / IDs .... left   (ALIGN.TEXT)
 *     Numbers / days ........ right  (ALIGN.NUMBER)
 *     Currency ($) .......... right  (ALIGN.CURRENCY)
 *     Dates ................. center (ALIGN.DATE)
 *     Status / badges ....... center (ALIGN.STATUS)
 *
 *   Number formatting (use exported fmtXxx helpers):
 *     Currency .... $1,234.56   fmtCurrency  (2dp, comma thousands)
 *     Days ........ 12.50       fmtDays      (2dp)
 *     Rate ........ $46.15      fmtCurrency  (2dp)
 *     Percent ..... 92.5%       fmtPercent   (1dp)
 *
 * Usage (PDF):
 *   const { doc, autoTable, ...layout } = await createBrandedPDF({ title: 'My Report' });
 *   const tableStartY = drawMetadataBlock(doc, metaLeft, metaRight, layout);
 *   drawSeparatorLine(doc, tableStartY, layout.leftX, layout.rightX);
 *   autoTable(doc, {
 *     startY: tableStartY,
 *     ...DEFAULT_TABLE_STYLES,
 *     margin: layout.tableMargin,
 *     head: [...], body: [...],
 *   });
 *   addBrandedFooter(doc, layout);
 *   doc.save('report.pdf');
 *
 * Usage (Excel):
 *   const { XLSX } = await loadExcelDependency();
 */
import weplLogo from '../assets/images/wepl_logo.png';

// ─── Layout Constants ────────────────────────────────────────────────────────

export const STANDARD_MARGIN = 14; // mm — all four sides, every report

export const COMPANY_NAME = 'Winner Engineering Pte Ltd';

export const HEADER_FILL_COLOR = [220, 38, 38];
export const HEADER_TEXT_COLOR = 255;

/** Column alignment constants — use in autoTable columnStyles.halign */
export const ALIGN = {
  TEXT: 'left',
  NUMBER: 'right',
  CURRENCY: 'right',
  DATE: 'center',
  STATUS: 'center',
};

/**
 * Standard autoTable styling. Spread into your autoTable call.
 * Do NOT set explicit cellWidth unless a column truly needs a fixed size —
 * autoTable auto-distributes columns to fill the available width between margins.
 */
export const DEFAULT_TABLE_STYLES = {
  headStyles: {
    fillColor: HEADER_FILL_COLOR,
    textColor: HEADER_TEXT_COLOR,
    fontSize: 7,
    fontStyle: 'bold',
    halign: 'center',
    cellPadding: 1.5,
  },
  styles: {
    fontSize: 7,
    cellPadding: 1.2,
    overflow: 'linebreak',
    lineColor: [220, 220, 220],
    lineWidth: 0.2,
  },
  theme: 'grid',
};

// ─── Number Formatters ───────────────────────────────────────────────────────

export function fmtCurrency(value) {
  if (value == null || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function fmtDays(value) {
  if (value == null || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return num.toFixed(2);
}

export function fmtPercent(value) {
  if (value == null || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return `${num.toFixed(1)}%`;
}

export function fmtDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Dependency Loaders (lazy, cached) ───────────────────────────────────────

let _jsPDF = null;
let _autoTable = null;
let _XLSX = null;

export const loadPDFDependencies = async () => {
  if (!_jsPDF || !_autoTable) {
    const jsPDFModule = await import('jspdf');
    const autoTableModule = await import('jspdf-autotable');
    _jsPDF = jsPDFModule.default || jsPDFModule.jsPDF;
    _autoTable = autoTableModule.default || autoTableModule.autoTable;
  }
  return { jsPDF: _jsPDF, autoTable: _autoTable };
};

export const loadExcelDependency = async () => {
  if (!_XLSX) {
    const XLSXModule = await import('xlsx');
    _XLSX = XLSXModule.default || XLSXModule;
  }
  return { XLSX: _XLSX };
};

export const loadAllDependencies = async () => {
  const [pdf, excel] = await Promise.all([
    loadPDFDependencies(),
    loadExcelDependency(),
  ]);
  return { ...pdf, ...excel };
};

// ─── Logo Loader (cached) ────────────────────────────────────────────────────

let _logoBase64Cache = null;

export async function loadLogoBase64() {
  if (_logoBase64Cache !== null) return _logoBase64Cache;

  if (weplLogo && weplLogo.startsWith('data:')) {
    _logoBase64Cache = weplLogo;
    return _logoBase64Cache;
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const handleLoad = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        _logoBase64Cache = canvas.toDataURL('image/png');
        resolve(_logoBase64Cache);
      } catch (e) {
        console.warn('Logo canvas conversion failed:', e);
        resolve(null);
      }
    };

    img.onload = handleLoad;
    img.onerror = () => resolve(null);
    img.src = weplLogo;

    if (img.complete && img.naturalWidth > 0) handleLoad();
  });
}

// ─── Branded PDF Creation ────────────────────────────────────────────────────

/**
 * Create a new jsPDF document with the standard branded header.
 *
 * Tables are expected to fill the full width between the 14mm margins.
 * Pass layout.tableMargin to autoTable's margin option.
 *
 * @param {Object} options
 * @param {string} options.title               - Report title
 * @param {string} [options.subtitle]          - Optional subtitle below company name
 * @param {string} [options.orientation='landscape']
 * @returns {{ doc, autoTable, headerEndY, sideMgn, pageW, pageH, leftX, rightX, lineH, tableMargin, contentWidth }}
 */
export async function createBrandedPDF({
  title,
  subtitle = null,
  orientation = 'landscape',
}) {
  const { jsPDF: JsPDF, autoTable: autoTableFn } = await loadPDFDependencies();
  const doc = new JsPDF({ orientation, unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const sideMgn = STANDARD_MARGIN;
  const leftX = sideMgn;
  const rightX = pageW - sideMgn;
  const lineH = 5;
  const contentWidth = pageW - 2 * STANDARD_MARGIN;
  const tableMargin = { left: STANDARD_MARGIN, right: STANDARD_MARGIN };

  const headerEndY = await drawBrandedHeader(doc, { title, subtitle });

  return {
    doc,
    autoTable: autoTableFn,
    headerEndY,
    sideMgn,
    pageW,
    pageH,
    leftX,
    rightX,
    lineH,
    tableMargin,
    contentWidth,
  };
}

/**
 * Draw the standard branded header (logo + title + company + optional subtitle)
 * on the CURRENT page of an existing document. Used by createBrandedPDF for the
 * first page and by multi-page reports (e.g. tiled posters) to repeat the same
 * header on pages added via doc.addPage().
 *
 * @param {jsPDF}  doc
 * @param {Object} options
 * @param {string} options.title
 * @param {string} [options.subtitle]
 * @returns {Promise<number>} The Y position (mm) directly below the header.
 */
export async function drawBrandedHeader(doc, { title, subtitle = null }) {
  const pageW = doc.internal.pageSize.getWidth();
  const leftX = STANDARD_MARGIN;

  const logoData = await loadLogoBase64();
  let headerY = 12;

  if (logoData) {
    doc.addImage(logoData, 'PNG', leftX, headerY, 28, 14);
  }

  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text(title, pageW / 2, headerY + 5, { align: 'center' });

  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(120);
  doc.text(COMPANY_NAME, pageW / 2, headerY + 11, { align: 'center' });

  if (subtitle) {
    doc.setFontSize(9);
    doc.text(subtitle, pageW / 2, headerY + 16, { align: 'center' });
    headerY += 22;
  } else {
    headerY += 20;
  }

  doc.setTextColor(0);
  return headerY;
}

// ─── Metadata Block ──────────────────────────────────────────────────────────

/**
 * Draw a two-column key-value metadata block (e.g. Employee Name: John Doe).
 *
 * @param {jsPDF}    doc
 * @param {Array}    metaLeft  - [['Label', 'Value'], ...]
 * @param {Array}    metaRight - [['Label', 'Value'], ...]
 * @param {Object}   layout    - { headerEndY, leftX, rightX, lineH } from createBrandedPDF
 * @param {Object}   [opts]
 * @param {number}   [opts.labelWidth=35] - Width reserved for the label column
 * @param {number}   [opts.fontSize=9]
 * @returns {number} The Y position after the metadata block (ready for a table)
 */
export function drawMetadataBlock(doc, metaLeft, metaRight, layout, opts = {}) {
  const { headerEndY, leftX, rightX, lineH } = layout;
  const { labelWidth = 35, fontSize = 9 } = opts;

  doc.setFontSize(fontSize);

  metaLeft.forEach(([label, value], i) => {
    doc.setFont(undefined, 'bold');
    doc.text(`${label}:`, leftX, headerEndY + i * lineH);
    doc.setFont(undefined, 'normal');
    doc.text(String(value ?? '-'), leftX + labelWidth, headerEndY + i * lineH);
  });

  metaRight.forEach(([label, value], i) => {
    doc.setFont(undefined, 'bold');
    doc.text(`${label}:`, rightX - 80, headerEndY + i * lineH);
    doc.setFont(undefined, 'normal');
    doc.text(String(value ?? '-'), rightX - 10, headerEndY + i * lineH, { align: 'right' });
  });

  const rows = Math.max(metaLeft.length, metaRight.length);
  return headerEndY + rows * lineH + 4;
}

// ─── Separator Line ──────────────────────────────────────────────────────────

export function drawSeparatorLine(doc, y, leftX, rightX) {
  doc.setDrawColor(200);
  doc.setLineWidth(0.3);
  doc.line(leftX, y - 2, rightX, y - 2);
}

// ─── Branded Footer (all pages) ──────────────────────────────────────────────

/**
 * Add the standard branded footer to every page of the document.
 * Call this AFTER all content has been drawn (including autoTable).
 *
 * @param {jsPDF}  doc
 * @param {Object} layout - { leftX, rightX } from createBrandedPDF
 */
export function addBrandedFooter(doc, layout) {
  const { leftX, rightX } = layout;
  const pageCount = doc.internal.getNumberOfPages();
  const now = getGeneratedTimestamp();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageH = doc.internal.pageSize.getHeight();
    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text(
      `This is a computer-generated report. Generated by WEPL HR Portal on ${now}.`,
      leftX, pageH - 9
    );
    doc.text('Confidential — For authorised use only.', leftX, pageH - 5);
    doc.text(`Page ${i} of ${pageCount}`, rightX, pageH - 7, { align: 'right' });
    doc.setTextColor(0);
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function getGeneratedTimestamp() {
  return new Date().toLocaleString('en-SG', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function sanitizeForFilename(str) {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9 _-]/g, '_')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}
